import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import FAQAccordion from "@/components/FAQAccordion";
import RelatedBlock from "@/components/RelatedBlock";
import BaseCard from "@/components/cards/BaseCard";
import ServiceCard from "@/components/cards/ServiceCard";
import { COMPANY } from "@/data/company";
import { ALL_SERVICES } from "@/data/nav";
import { trackPhoneClick } from "@/lib/track";

// Soft blurbs written for the index page — deliberately avoid response-time
// claims (no "within the hour", no "24/7 breakdown cover" statements).
const REGION_CARDS = [
  {
    slug: "cheshire",
    name: "Cheshire",
    blurb:
      "Our home patch — including Warrington, Chester, Crewe, Runcorn and the A55 industrial corridor.",
  },
  {
    slug: "greater-manchester",
    name: "Greater Manchester",
    blurb:
      "Ten-borough footprint — Manchester, Salford, Bolton, Stockport and the M60 orbital estates.",
  },
  {
    slug: "merseyside",
    name: "Merseyside",
    blurb:
      "Liverpool, St Helens, Wirral and the wider Merseyside industrial and logistics estate.",
  },
  {
    slug: "lancashire",
    name: "Lancashire",
    blurb:
      "Preston, Blackburn, Blackpool and the Central Lancashire manufacturing and logistics belt.",
  },
];

const TOWN_CARDS = [
  { slug: "manchester", name: "Manchester", region: "Greater Manchester" },
  { slug: "liverpool", name: "Liverpool", region: "Merseyside" },
  { slug: "warrington", name: "Warrington", region: "Cheshire" },
  { slug: "chester", name: "Chester", region: "Cheshire" },
  { slug: "preston", name: "Preston", region: "Lancashire" },
  { slug: "salford", name: "Salford", region: "Greater Manchester" },
  { slug: "bolton", name: "Bolton", region: "Greater Manchester" },
  { slug: "st-helens", name: "St Helens", region: "Merseyside" },
];

const RELEVANT_SERVICE_SLUGS = [
  "24-7-emergency-breakdown-callout",
  "planned-preventive-maintenance",
  "industrial-electrical-installation",
  "eicr-and-fixed-wire-testing",
  "control-panel-design-and-build",
  "machine-installation-and-commissioning",
];

const FAQ = [
  {
    q: "Where is Lutrom Engineering based?",
    a: "Our workshop is at Unit 28, Winnington Business Park, Northwich, Cheshire. All our engineers, panel builds, workshop refurbishments and admin operate from this single site — which gives us one accountable point of contact for the whole North West footprint.",
  },
  {
    q: "How far do you cover?",
    a: "We cover the North West of England — Cheshire, Greater Manchester, Merseyside and Lancashire — with our four region hub pages describing the sector mix and the town pages breaking each core town down in more detail. Where a project is genuinely outside the North West, we're happy to discuss it, but our routine coverage sits inside these four counties.",
  },
  {
    q: "How do you handle response times?",
    a: "Response performance depends on distance from our Northwich base and current engineer availability. We give an honest ETA on the phone when the callout is logged — no fixed promises we can't keep. Sites on a PPM contract with agreed reactive priority typically see faster attendance than ad-hoc callouts.",
  },
  {
    q: "Can you handle multi-site accounts across the whole North West?",
    a: "Yes. Multi-site accounts (retail estates, hospitality groups, MATs, commercial landlord portfolios) are consolidated into a single register with per-site schedules, one invoice and portfolio-level reporting for head office. Individual sites keep their own visit schedules; head office sees the whole picture.",
  },
  {
    q: "Do you cover towns not listed on your town pages?",
    a: "Yes. The town pages cover our most-requested locations, but our footprint takes in the wider North West including Wirral, Crewe, Macclesfield, Blackburn and everywhere in between. If your site is in the North West and isn't listed, tell us at enquiry and we'll confirm fit.",
  },
];

const CoverageIndex = () => {
  const services = RELEVANT_SERVICE_SLUGS
    .map((slug) => ALL_SERVICES.find((s) => s.slug === slug))
    .filter(Boolean);

  return (
    <div data-testid="coverage-index-page">
      <Seo
        title="Coverage across the North West — Regions & Towns"
        description="Industrial and commercial engineering coverage across Cheshire, Greater Manchester, Merseyside and Lancashire. Single-supplier support from Lutrom's Northwich workshop."
        path="/coverage"
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <PhotoBackdrop src="/gallery/workshop.jpg" position="center 40%" />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 relative">
          <p className="eyebrow">Coverage · North West England</p>
          <h1
            className="font-display mt-6 text-white max-w-4xl"
            style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1 }}
            data-testid="coverage-index-h1"
          >
            Industrial engineering coverage across the North West.
          </h1>
          <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[color:var(--lt-text-dim)]">
            Mechanical, electrical and controls engineering for industrial and
            commercial sites across Cheshire, Greater Manchester, Merseyside and
            Lancashire — one team, one number, one point of accountability.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={COMPANY.phone.tel}
              onClick={() => trackPhoneClick("coverage_index_hero")}
              className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="coverage-index-hero-call"
            >
              <Phone size={16} /> Talk to an engineer
            </a>
            <Link
              to="/contact"
              className="btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="coverage-index-hero-enquire"
            >
              Send an enquiry <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <Breadcrumb items={[{ label: "Coverage" }]} />

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="eyebrow">Where we work</p>
          <h2
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
          >
            One workshop, four counties, dozens of towns.
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-[15.5px] leading-relaxed text-[color:var(--lt-text-dim)]">
            Lutrom Engineering operates from a single workshop in Northwich,
            Cheshire — right in the middle of the North West&apos;s industrial belt.
            From that base we cover Cheshire, Greater Manchester, Merseyside and
            Lancashire with mechanical, electrical and controls engineering for
            manufacturing, logistics, F&amp;B, pharma, retail, commercial,
            education and leisure clients. This page is the single starting
            point for the coverage cluster — four region hubs describe each
            county in more depth, and eight town pages break the most-requested
            locations down further. Response performance depends on distance
            from our Northwich base and current engineer availability; we give
            an honest ETA on the phone when the callout is logged, and PPM
            contracts with agreed reactive priority typically see faster
            attendance than ad-hoc callouts.
          </p>
        </div>
      </section>

      {/* REGION HUBS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Region hubs</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
        >
          The four counties we cover.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {REGION_CARDS.map((r) => (
            <BaseCard
              key={r.slug}
              to={`/coverage/${r.slug}`}
              eyebrow="Region hub"
              title={r.name}
              blurb={r.blurb}
              testid={`coverage-index-region-${r.slug}`}
            />
          ))}
        </div>
      </section>

      {/* TOWN GRID */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Town pages</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
        >
          Core towns we cover.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TOWN_CARDS.map((t) => (
            <BaseCard
              key={t.slug}
              to={`/coverage/${t.slug}`}
              eyebrow={t.region}
              title={t.name}
              blurb={`Industrial and commercial engineering coverage in ${t.name}.`}
              testid={`coverage-index-town-${t.slug}`}
            />
          ))}
        </div>
        <p className="mt-6 text-[13.5px] text-[color:var(--lt-text-mute)]">
          Not seeing your town? Our region hubs cover the wider footprint —
          Wirral, Crewe, Macclesfield, Blackburn and dozens more sit inside one
          of the four counties above.
        </p>
      </section>

      {/* COVERAGE NOTE + HQ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="glass-card rounded-xl p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">How we cover the North West</p>
            <h2
              className="font-display mt-4 text-white"
              style={{ fontSize: "clamp(24px, 2.8vw, 34px)", lineHeight: 1.05 }}
            >
              One workshop, one accountable team.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
              Every job — planned or reactive — is dispatched from our Northwich
              workshop. Response performance depends on distance from our base
              and current engineer availability, and we give an honest ETA on
              the phone when the callout is logged. For sites further from our
              Northwich base, planned PPM visits can be grouped where this
              suits the customer and the asset criticality.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div
              className="rounded-lg p-6"
              style={{
                border: "1px solid var(--lt-border-strong)",
                background: "rgba(6, 11, 24, 0.55)",
              }}
              data-testid="coverage-index-hq-block"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[color:var(--lt-blue-soft)] flex items-center gap-2">
                <MapPin size={13} /> Head office
              </p>
              <p className="mt-3 text-white font-display text-[20px] leading-tight">
                Lutrom Engineering
              </p>
              <address className="not-italic mt-2 text-[14.5px] leading-relaxed text-[color:var(--lt-text-dim)]">
                Unit 28<br />
                Winnington Business Park<br />
                Winnington Avenue<br />
                Northwich<br />
                CW8 4DL
              </address>
              <a
                href={COMPANY.phone.tel}
                onClick={() => trackPhoneClick("coverage_index_hq")}
                className="mt-4 inline-flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--lt-blue-bright)]"
                data-testid="coverage-index-hq-phone"
              >
                <Phone size={13} /> {COMPANY.phone.display}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* RELEVANT SERVICES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Services available across coverage</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
        >
          What we deliver everywhere we cover.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s) => (
            <ServiceCard
              key={s.slug}
              slug={s.slug}
              name={s.name}
              blurb={s.blurb}
              categoryName={s.categoryName}
            />
          ))}
        </div>
        <div className="mt-6">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[13.5px] text-[color:var(--lt-blue-bright)] font-semibold"
          >
            View all services <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow">Coverage FAQ</p>
            <h2
              className="font-display mt-4 text-white"
              style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}
            >
              Common questions.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={FAQ} testIdPrefix="coverage-index-faq" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTABanner
        variant="standard"
        title="Need an engineer somewhere in the North West? Let's talk."
        body="Send us a short outline of the site and what you're trying to solve. We'll come back with an honest view on whether we're the right fit — and, if we are, a fixed-price way forward."
        phoneLocation="coverage_index_bottom"
      />

      {/* RELATED — services + industries pivot */}
      <RelatedBlock
        title="Explore more"
        groups={[
          {
            eyebrow: "Related",
            items: [
              <BaseCard
                key="services"
                to="/services"
                eyebrow="Services"
                title="All services"
                blurb="Every mechanical, electrical, controls and compliance service we offer, grouped by discipline."
                testid="coverage-index-related-services"
              />,
              <BaseCard
                key="industries"
                to="/industries/manufacturing"
                eyebrow="Industries"
                title="Sectors we support"
                blurb="How our engineering support fits the seven core sectors we work with — from manufacturing to leisure."
                testid="coverage-index-related-industries"
              />,
              <BaseCard
                key="about"
                to="/about"
                eyebrow="About Lutrom"
                title="Who we are"
                blurb="One workshop, one accountable team, ISO 9001 / 14001 / 45001 across the board."
                testid="coverage-index-related-about"
              />,
            ],
          },
        ]}
      />
    </div>
  );
};

export default CoverageIndex;
